import React, { useState } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ScheduleIcon from '@mui/icons-material/Schedule';

import PharmacyTable from './PharmacyTable';
import RevenueEntryManager from './revenueEntry/RevenueEntryManager';
import RegionManager from './RegionManager';
import MathReportFields from '../mathReports/MathReportTable';
import { TABLES_FIELDS_API, TABLES_FIELDS_PERMS } from '../mathReports/fields/fieldsApi';
import { usePermissions } from '../../../utils/usePermissions';

interface ReportsProps {
  setSelectedIndex?: (index: number) => void;
}

type TabId = 'revenue' | 'revenue_entry' | 'regions' | 'math_fields';

interface TabItem {
  id: TabId;
  label: string;
  hint: string;
  icon: React.ReactElement;
  /** Право на просмотр вкладки */
  perm: string;
}

// Порядок вкладок = порядок кнопок; вкладка без права на просмотр не показывается
const TABS: TabItem[] = [
  {
    id: 'revenue',
    label: 'Таблица выручки',
    hint: 'Настройка колонок и формул формы отчетов аптек',
    icon: <TableChartIcon />,
    perm: 'tables.revenue.view'
  },
  {
    id: 'revenue_entry',
    label: 'Окно ввода',
    hint: 'Время ввода данных аптеками, продление, разблокировка и дата ввода',
    icon: <ScheduleIcon />,
    perm: 'tables.revenue.entry.view'
  },
  {
    id: 'regions',
    label: 'Список регионов',
    hint: 'Справочник городов и регионов аптечной сети',
    icon: <LocationCityIcon />,
    perm: 'tables.regions.view'
  },
  {
    id: 'math_fields',
    label: 'Поля в мат отчете',
    hint: 'Настройка полей и колонок материального отчета',
    icon: <ViewColumnIcon />,
    perm: 'tables.math_fields.view'
  }
];

const BDTableMenu: React.FC<ReportsProps> = () => {
  const { hasPerm } = usePermissions();
  const [selected, setSelected] = useState<TabId | null>(null);

  const available = TABS.filter((tab) => hasPerm(tab.perm));
  if (available.length === 0) return null;

  // Выбранная вкладка могла потерять право: тогда открывается первая из доступных
  const active = available.some((tab) => tab.id === selected) ? selected : available[0].id;

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto', p: { xs: 1.5, sm: 2 } }}>
      {/* Вкладки модуля: активная выделяется заливкой без теней */}
      <Box
        sx={{
          mb: 2.5,
          p: 1,
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: 1,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '12px'
        }}
      >
        {available.map((tab) => {
          const isActive = tab.id === active;
          return (
            <Tooltip key={tab.id} title={tab.hint} enterDelay={800} arrow>
              <Button
                variant={isActive ? 'contained' : 'text'}
                color="primary"
                onClick={() => setSelected(tab.id)}
                startIcon={tab.icon}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                  py: 0.8,
                  boxShadow: 'none',
                  color: isActive ? '#ffffff' : '#64748b',
                  '&:hover': { boxShadow: 'none', backgroundColor: isActive ? undefined : '#edf2f7' }
                }}
              >
                {tab.label}
              </Button>
            </Tooltip>
          );
        })}
      </Box>

      {active === 'revenue' && <PharmacyTable />}
      {active === 'revenue_entry' && <RevenueEntryManager />}
      {active === 'regions' && <RegionManager />}
      {active === 'math_fields' && <MathReportFields api={TABLES_FIELDS_API} perms={TABLES_FIELDS_PERMS} />}
    </Box>
  );
};

export default BDTableMenu;
